import { useEffect, useState } from 'react';

import { Alert, SelectChangeEvent, Skeleton, Stack } from '@mui/material';
import { useChannel } from 'ably/react';

import { CfsApi } from '@/api';
import { AppModal, BaseModalProps, Select, SelectItem } from '@/components';
import { LocalStorageItems } from '@/constants';
import { AgencyType, IAgency } from '@/models';
import {
  ConfirmTypes,
  useAgencyStore,
  useDataStore,
  useToastStore,
  useUserStore,
} from '@/store';

interface IClosureReasonModal extends BaseModalProps {
  cfsId: string;
  assignedAgencies: string[] | undefined;
  updateCfsClosedState?: (isClosed: boolean, reason: string) => void;
  onBeforeClosedSuccess?: () => void | Promise<void>;
  onClosedSuccess?: () => void;
}

const ClosureReasonModal = ({
  open,
  cfsId,
  onClose,
  assignedAgencies,
  updateCfsClosedState,
  onBeforeClosedSuccess,
  onClosedSuccess,
}: IClosureReasonModal) => {
  const { updateToast } = useToastStore();
  const { cfsCloseReasons } = useDataStore();
  const { account } = useUserStore();
  const { agencies } = useAgencyStore();

  const [reason, setReason] = useState('');
  const [isLoadingOfficers, setIsLoadingOfficers] = useState(false);

  const [agencyOfficersMap, setAgencyOfficersMap] = useState<
    Record<string, SelectItem[]>
  >({});
  const [selectedAgencyOfficers, setSelectedAgencyOfficers] = useState<
    Record<string, string>
  >({});

  const { userAgencyId } = useUserStore();
  const reasonOptions = cfsCloseReasons.map((item) => ({
    value: String(item._id),
    label: item.reason,
  }));
  const { channel } = useChannel(`account:${account?._id}:cfs:${cfsId}`);

  const assignedAgencyObjects: IAgency[] = agencies.filter((a) =>
    assignedAgencies?.includes(a._id as string),
  );

  useEffect(() => {
    if (cfsId) {
      fetchRespondingOfficersPerAgency();
    }
  }, []);

  const fetchRespondingOfficersPerAgency = async () => {
    try {
      setIsLoadingOfficers(true);
      const response = await CfsApi.getOne(cfsId);
      const officersByAgency: Record<string, SelectItem[]> = {};
      const primaryOfficersByAgency: Record<string, string> = {};

      for (const shift of response.data.assignedUnitShifts) {
        const agencyId = shift.unitShift.unit?.agency;
        if (!agencyId) continue;

        if (!officersByAgency[agencyId]) {
          officersByAgency[agencyId] = [{ value: 'n/a', label: 'N/A' }];
        }

        shift.unitShift.users?.forEach((user) => {
          const userId = user._id ?? '';
          const userName = user.fullName ?? '';

          if (!officersByAgency[agencyId].some((o) => o.value === userId)) {
            officersByAgency[agencyId].push({ value: userId, label: userName });
          }

          const primaryOfficer = response.data.primaryOfficer;

          if (
            typeof primaryOfficer === 'object' &&
            primaryOfficer?._id === userId &&
            !primaryOfficersByAgency[agencyId]
          ) {
            primaryOfficersByAgency[agencyId] = userId;
          }
        });
      }

      setAgencyOfficersMap(officersByAgency);
      setSelectedAgencyOfficers(primaryOfficersByAgency);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      setIsLoadingOfficers(false);
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleAgencyOfficerChange = (agencyId: string, officerId: string) => {
    setSelectedAgencyOfficers((prev) => ({
      ...prev,
      [agencyId]: officerId,
    }));
  };

  const handleReasonChange = (e: SelectChangeEvent<unknown>) => {
    const newReason = e.target.value as string;
    setReason(newReason);
    if (newReason) {
      submitClosure(newReason);
    }
  };

  const submitClosure = (selectedReason: string) => {
    if (!selectedReason) return;

    const policeAgencyPrimaryOfficers = assignedAgencyObjects
      .filter((agency) => agency.type === AgencyType.POLICE)
      .map((agency) => {
        const selected = selectedAgencyOfficers[agency._id ?? ''];
        return {
          agency: agency._id as string,
          primaryOfficer: selected && selected !== 'n/a' ? selected : null,
        };
      });

    if (userAgencyId) {
      localStorage.setItem(LocalStorageItems.CurrentAgencyId, userAgencyId);
    }

    CfsApi.closeCFS(cfsId, selectedReason, '', policeAgencyPrimaryOfficers)
      .then(async () => {
        await onBeforeClosedSuccess?.();
        handleClose();
        updateCfsClosedState?.(true, selectedReason);
        onClosedSuccess?.();

        channel?.publish(`closed:cfs:${cfsId}`, {
          id: cfsId,
          event: 'cfsClosed',
        });

        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'CFS record closed successfully',
        });
      })
      .catch((err: any) => {
        updateToast({ open: true, message: err.message });
      });
  };

  const autoTask = account?.settings.features?.isEnabledAutoTaskCreation;
  const autoCase = account?.settings.features?.isEnabledAutomaticRMSCreation;
  const triggerText =
    autoTask && autoCase
      ? 'automatic task and case creation'
      : autoTask
      ? 'automatic task creation'
      : 'automatic case creation';

  const shouldShowAnyDropdown = assignedAgencyObjects.some((agency) => {
    const isPolice = agency.type === 'POLICE';
    const officerOptions = agencyOfficersMap[agency._id ?? ''];
    return (
      (autoTask || autoCase) &&
      isPolice &&
      officerOptions &&
      officerOptions.length > 1 &&
      agency._id === userAgencyId
    );
  });

  return (
    <AppModal open={open} onClose={handleClose} title="Set disposition">
      <Stack spacing={2}>
        {isLoadingOfficers &&
          assignedAgencyObjects.map((age) => {
            if (age.type !== 'POLICE') return null;
            return <Skeleton key={age._id} height={46} variant="rounded" />;
          })}

        {assignedAgencyObjects.map((agency) => {
          const isPolice = agency.type === 'POLICE';
          const officerOptions = agencyOfficersMap[agency._id ?? ''];
          const isEnabledTaskCreation =
            account?.settings.features?.isEnabledAutoTaskCreation;
          const isEnabledCaseCreation =
            account?.settings.features?.isEnabledAutomaticRMSCreation;

          const shouldShowDropdown =
            (isEnabledTaskCreation || isEnabledCaseCreation) &&
            isPolice &&
            officerOptions &&
            officerOptions.length > 1 &&
            agency._id === userAgencyId;

          if (!shouldShowDropdown) return null;

          return (
            <Select
              key={agency._id}
              label={`Select Primary Officer for ${agency.name}`}
              value={selectedAgencyOfficers[agency._id ?? ''] ?? 'n/a'}
              items={officerOptions}
              onChange={(e) =>
                handleAgencyOfficerChange(
                  agency._id as string,
                  e.target.value as string,
                )
              }
            />
          );
        })}
        {shouldShowAnyDropdown && (
          <Alert severity="info">
            Assigning a Primary Officer will trigger {triggerText} for that
            officer.
          </Alert>
        )}

        <Select
          label="Disposition"
          value={reason}
          items={reasonOptions}
          onChange={handleReasonChange}
        />
      </Stack>
    </AppModal>
  );
};

export default ClosureReasonModal;
